import apiRequest from "src/utils/api-request";
import withApiPath from "src/utils/with-api-path";

export interface OrphanTransaction {
	id: number;
	transactionHash?: string;
	senderAddress?: string;
	recipientAddress: string;
	amount: string;
	factAmount?: string;
	currency: string;
	blockchain: string;
	networkId: string;
	isTest: boolean;
	status: string;
	createdAt: string;
}

export interface MerchantSuggestion {
	merchantId: number;
	merchantUuid: string;
	merchantName: string;
	orderId: number;
	orderUuid: string;
	orderStatus: string;
	orderCreatedAt: string;
}

export interface ExpiredOrderCandidate {
	orderId: number;
	orderUuid: string;
	merchantOrderId: string;
	merchantId: number;
	merchantUuid: string;
	merchantName: string;
	status: string;
	cryptoCurrency: string;
	cryptoAmount: string;
	fiatCurrency: string;
	fiatPrice: string;
	receivedAmount: string;
	expiresAt: string;
	createdAt: string;
	isTest: boolean;
}

export interface EligiblePaymentCandidate {
	paymentId: number;
	paymentUuid: string;
	merchantOrderId: string;
	merchantId: number;
	merchantUuid: string;
	merchantName: string;
	status: string;
	type: string;
	fiatCurrency: string;
	fiatPrice: string;
	expiresAt?: string;
	createdAt: string;
	isTest: boolean;
}

export interface OrphanTransactionDetail extends OrphanTransaction {
	suggestion?: MerchantSuggestion;
	expiredOrders: ExpiredOrderCandidate[];
	eligiblePayments: EligiblePaymentCandidate[];
	netAmountAfterFee: string;
	estimatedFeeRate: number;
	estimatedServiceFee: string;
}

export interface ListOrphanTransactionsParams {
	limit?: number;
	offset?: number;
	search?: string;
	currency?: string;
	blockchain?: string;
}

export interface PaginatedOrphanTransactions {
	items: OrphanTransaction[];
	totalCount: number;
	limit: number;
	offset: number;
}

export interface LinkOrphanToOrderRequest {
	orderUuid: string;
	reason: string;
	totpCode: string;
}

export interface LinkOrphanToPaymentRequest {
	paymentUuid: string;
	reason: string;
	totpCode: string;
}

export interface CreditOrphanToMerchantRequest {
	merchantUuid: string;
	reason: string;
	totpCode: string;
}

export interface OrphanActionResponse {
	success: boolean;
	message: string;
	transactionId: number;
	merchantId: number;
	creditedAmount: string;
	serviceFee: string;
}

export interface OrphanCreditEstimate {
	transactionId: number;
	merchantId: number;
	merchantUuid: string;
	merchantName: string;
	currency: string;
	received: string;
	feeRate: number;
	serviceFee: string;
	merchantCredit: string;
}

const orphanTransactionProvider = {
	async list(
		params: ListOrphanTransactionsParams = {},
	): Promise<PaginatedOrphanTransactions> {
		const response = await apiRequest.get<PaginatedOrphanTransactions>(
			withApiPath("/orphan-transactions"),
			{
				params: {
					limit: params.limit ?? 50,
					offset: params.offset ?? 0,
					search: params.search ?? "",
					currency: params.currency ?? "",
					blockchain: params.blockchain ?? "",
				},
			},
		);
		return response.data;
	},

	async get(id: number): Promise<OrphanTransactionDetail> {
		const response = await apiRequest.get<OrphanTransactionDetail>(
			withApiPath(`/orphan-transactions/${id}`),
		);
		return response.data;
	},

	async linkToOrder(
		id: number,
		params: LinkOrphanToOrderRequest,
	): Promise<OrphanActionResponse> {
		const response = await apiRequest.post<OrphanActionResponse>(
			withApiPath(`/orphan-transactions/${id}/link-order`),
			{
				orderUuid: params.orderUuid,
				reason: params.reason,
			},
			{
				headers: {
					"X-2FA-Code": params.totpCode,
				},
			},
		);
		return response.data;
	},

	async linkToPayment(
		id: number,
		params: LinkOrphanToPaymentRequest,
	): Promise<OrphanActionResponse> {
		const response = await apiRequest.post<OrphanActionResponse>(
			withApiPath(`/orphan-transactions/${id}/link-payment`),
			{
				paymentUuid: params.paymentUuid,
				reason: params.reason,
			},
			{
				headers: {
					"X-2FA-Code": params.totpCode,
				},
			},
		);
		return response.data;
	},

	async creditToMerchant(
		id: number,
		params: CreditOrphanToMerchantRequest,
	): Promise<OrphanActionResponse> {
		const response = await apiRequest.post<OrphanActionResponse>(
			withApiPath(`/orphan-transactions/${id}/credit-merchant`),
			{
				merchantUuid: params.merchantUuid,
				reason: params.reason,
			},
			{
				headers: {
					"X-2FA-Code": params.totpCode,
				},
			},
		);
		return response.data;
	},

	async estimateForMerchant(
		id: number,
		merchantUuid: string,
	): Promise<OrphanCreditEstimate> {
		const response = await apiRequest.get<OrphanCreditEstimate>(
			withApiPath(`/orphan-transactions/${id}/estimate`),
			{ params: { merchantUuid } },
		);
		return response.data;
	},
};

export default orphanTransactionProvider;
